/* eslint-disable @typescript-eslint/no-unused-vars */
// src/pages/FAQ.tsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDarkMode } from "../context/DarkModeContext";
import { Helmet } from "react-helmet-async";
interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ: React.FC = () => {
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const faqData: FAQItem[] = [
    // Account & Registration
    {
      question: "How do I create a FindWork account?",
      answer:
        'Click the "Sign Up" button on the top right corner of our homepage. You can register using your email address, Google account, or LinkedIn profile. Follow the verification steps to complete your registration.',
      category: "account",
    },
    {
      question: "Can I change my email address after registration?",
      answer:
        'Yes, you can update your email address in the "Account Settings" section. You will need to verify your new email address before the change is complete.',
      category: "account",
    },
    {
      question: "How do I reset my password?",
      answer:
        'Click "Forgot Password" on the login page, enter your email address, and we\'ll send you a password reset link. The link will expire in 1 hour for security reasons.',
      category: "account",
    },
    {
      question: "Can I delete my FindWork account?",
      answer:
        'Yes, you can permanently delete your account in the "Account Settings" under "Privacy & Data". Please note that this action is irreversible and will remove all your data from our platform.',
      category: "account",
    },

    // Job Search & Applications
    {
      question: "How do I search for jobs on FindWork?",
      answer:
        'Use the search bar at the top of the "Find Jobs" page. You can filter by job title, company, location, salary range, experience level, and job type (full-time, part-time, remote, etc.).',
      category: "job-search",
    },
    {
      question: "How many jobs can I apply to?",
      answer:
        "Free users can apply to up to 10 jobs per month. Premium subscribers have unlimited applications and access to exclusive job listings.",
      category: "job-search",
    },
    {
      question: "Can I save jobs to apply later?",
      answer:
        'Yes, click the "Save" button on any job listing to add it to your saved jobs. You can access your saved jobs from your dashboard.',
      category: "job-search",
    },
    {
      question: "How do I know if an employer has viewed my application?",
      answer:
        "You will receive a notification when an employer views your application. Premium users also get detailed analytics on application views and response rates.",
      category: "job-search",
    },
    {
      question: "Can I update my application after submitting it?",
      answer:
        "Once submitted, you cannot modify the application. However, you can withdraw your application and submit a new one if the job posting is still open.",
      category: "job-search",
    },

    // Resume & Profile
    {
      question: "What file formats can I upload for my resume?",
      answer:
        "We accept PDF, DOC, and DOCX files. For best results, we recommend using PDF format to preserve your formatting across different devices.",
      category: "resume",
    },
    {
      question: "Can I have multiple resumes on my profile?",
      answer:
        "Yes, premium users can upload up to 5 different resumes and choose which one to use for each application. Free users can have one active resume.",
      category: "resume",
    },
    {
      question: "How do I make my profile stand out to employers?",
      answer:
        "Complete all profile sections, add a professional photo, write a compelling summary, list all relevant skills, and get recommendations from previous colleagues or managers.",
      category: "resume",
    },
    {
      question: "Is my profile visible to all employers?",
      answer:
        'Your basic profile is visible to employers you apply to. You can adjust your privacy settings to control who can see your profile in the "Privacy & Data" section.',
      category: "resume",
    },

    // Subscription & Payments
    {
      question: "What are the benefits of a premium subscription?",
      answer:
        "Premium subscribers get unlimited job applications, access to exclusive job listings, advanced analytics, priority customer support, resume reviews, and the ability to message employers directly.",
      category: "subscription",
    },
    {
      question: "How do I cancel my subscription?",
      answer:
        'Go to "Account Settings" > "Subscription" and click "Cancel Subscription". Your premium features will remain active until the end of your current billing period.',
      category: "subscription",
    },
    {
      question: "Can I get a refund for my subscription?",
      answer:
        "We offer a 14-day money-back guarantee for new subscriptions. Refund requests after 14 days are handled on a case-by-case basis. Contact our support team for assistance.",
      category: "subscription",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept credit/debit cards (Visa, MasterCard, American Express), PayPal, M-Pesa (Kenya), and various other local payment methods depending on your region.",
      category: "subscription",
    },
    {
      question: "How do I update my payment information?",
      answer:
        'Navigate to "Account Settings" > "Billing" and click "Update Payment Method". You can add, remove, or modify your payment details there.',
      category: "subscription",
    },

    // Technical Issues
    {
      question: "The website is not loading properly. What should I do?",
      answer:
        "Try clearing your browser cache and cookies, or use a different browser. If the issue persists, contact our support team with details about your device and browser.",
      category: "technical",
    },
    {
      question: "I'm not receiving email notifications. What could be wrong?",
      answer:
        'Check your spam folder and ensure "notifications@FindWork.com" is in your contacts. Also verify your email settings in your account preferences.',
      category: "technical",
    },
    {
      question: "How do I enable two-factor authentication?",
      answer:
        'Go to "Account Settings" > "Security" and toggle on "Two-Factor Authentication". You can choose to receive codes via email or authenticator app.',
      category: "technical",
    },
    {
      question: "Is FindWork available as a mobile app?",
      answer:
        "Yes, FindWork is available on both iOS and Android. You can download it from the App Store or Google Play Store.",
      category: "technical",
    },
  ];

  const categories = [
    { id: "all", name: "All Questions", count: faqData.length },
    {
      id: "account",
      name: "Account & Registration",
      count: faqData.filter((f) => f.category === "account").length,
    },
    {
      id: "job-search",
      name: "Job Search & Applications",
      count: faqData.filter((f) => f.category === "job-search").length,
    },
    {
      id: "resume",
      name: "Resume & Profile",
      count: faqData.filter((f) => f.category === "resume").length,
    },
    {
      id: "subscription",
      name: "Subscription & Payments",
      count: faqData.filter((f) => f.category === "subscription").length,
    },
    {
      id: "technical",
      name: "Technical Issues",
      count: faqData.filter((f) => f.category === "technical").length,
    },
  ];

  const toggleItem = (index: number) => {
    setOpenItems((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index],
    );
  };

  const filteredFaqs = faqData.filter((faq) => {
    const matchesSearch =
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleContactSupport = () => {
    navigate("/contact-support");
  };

  // const handleNavigateToHelp = () => {
  //   navigate("/help-center");
  // };

  const handleNavigateToCommunity = () => {
    // In a real app, this would navigate to community page
    navigate("/community");
  };

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? "bg-gray-900 text-gray-100"
          : "bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900"
      }`}
    > 
      <Helmet>
        <title>FindWork - Frequently Asked Questions</title>
        <meta
          name="description"
          content="Find quick answers to common questions about FindWork. Can't find what you're looking for? Contact our support team."
        />
        <link rel="canonical" href="https://findwork.ltd/faq" />
      </Helmet>
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1
            className={`text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r ${
              isDark
                ? "from-blue-400 to-blue-600 text-white"
                : "from-sky-500 to-blue-600 text-transparent bg-clip-text"
            }`}
          >
            Frequently Asked Questions
          </h1>
          <p
            className={`text-lg max-w-2xl mx-auto ${
              isDark ? "text-gray-400" : "text-blue-700"
            }`}
          >
            Find quick answers to common questions about FindWork. Can't find
            what you're looking for? Contact our support team.
          </p>
        </div>

        {/* Search Bar */}
        <div
          className={`mb-8 p-6 rounded-xl shadow-lg ${
            isDark
              ? "bg-gray-800 border border-gray-700"
              : "bg-white border border-blue-200 shadow-blue-100"
          }`}
        >
          <div className="relative">
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-3 pl-12 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-blue-200 text-gray-900 placeholder-blue-400"
              }`}
            />
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
              <svg
                className={`w-5 h-5 ${isDark ? "text-gray-400" : "text-blue-500"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div
              className={`rounded-xl shadow-lg p-6 sticky top-8 ${
                isDark
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-white border border-blue-200 shadow-blue-100"
              }`}
            >
              <h2
                className={`text-lg font-semibold mb-4 ${
                  isDark ? "text-white" : "text-blue-800"
                }`}
              >
                Categories
              </h2>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex justify-between items-center ${
                      selectedCategory === category.id
                        ? isDark
                          ? "bg-blue-600 text-white"
                          : "bg-gradient-to-r from-sky-500 to-blue-600 text-white"
                        : isDark
                          ? "text-gray-300 hover:bg-gray-700"
                          : "text-blue-800 hover:bg-blue-50"
                    }`}
                  >
                    <span className="text-sm font-medium">{category.name}</span>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        selectedCategory === category.id
                          ? "bg-white text-blue-600"
                          : isDark
                            ? "bg-gray-600 text-gray-300"
                            : "bg-blue-100 text-blue-600"
                      }`}
                    >
                      {category.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact Card */}
            <div
              className={`mt-6 rounded-xl shadow-lg p-6 ${
                isDark
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-white border border-blue-200 shadow-blue-100"
              }`}
            >
              <h3
                className={`font-semibold mb-3 ${
                  isDark ? "text-white" : "text-blue-800"
                }`}
              >
                Still need help?
              </h3>
              <p
                className={`text-sm mb-4 ${
                  isDark ? "text-gray-400" : "text-blue-700"
                }`}
              >
                Can't find the answer you're looking for? Our support team is
                here to help.
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleContactSupport}
                  className={`block w-full text-center px-4 py-2 rounded-lg font-medium transition-colors ${
                    isDark
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
                  }`}
                >
                  Contact Support
                </button>
                <Link
                  to="/help-Center"
                  className={`block w-full text-center px-4 py-2 rounded-lg font-medium border transition-colors ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-blue-200 text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  Visit Help Center
                </Link>
              </div>
            </div>
          </div>

          {/* FAQ Items */}
          <div className="lg:col-span-3">
            <div
              className={`rounded-xl shadow-lg ${
                isDark
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-white border border-blue-200 shadow-blue-100"
              }`}
            >
              {/* Results Header */}
              <div
                className={`p-6 border-b ${
                  isDark ? "border-gray-700" : "border-blue-200"
                }`}
              >
                <div className="flex justify-between items-center">
                  <h2
                    className={`text-xl font-semibold ${
                      isDark ? "text-white" : "text-blue-800"
                    }`}
                  >
                    {selectedCategory === "all"
                      ? "All Questions"
                      : categories.find((c) => c.id === selectedCategory)?.name}
                  </h2>
                  <span
                    className={`text-sm ${
                      isDark ? "text-gray-400" : "text-blue-600"
                    }`}
                  >
                    {filteredFaqs.length}{" "}
                    {filteredFaqs.length === 1 ? "question" : "questions"}
                  </span>
                </div>
              </div>

              {/* FAQ List */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredFaqs.length > 0 ? (
                  filteredFaqs.map((faq, index) => (
                    <div
                      key={index}
                      className={`transition-colors ${
                        isDark ? "hover:bg-gray-750" : "hover:bg-blue-50"
                      }`}
                    >
                      <button
                        onClick={() => toggleItem(index)}
                        className={`w-full text-left p-6 flex justify-between items-center transition-colors ${
                          isDark ? "text-white" : "text-blue-900"
                        }`}
                      >
                        <span className="font-medium pr-4">{faq.question}</span>
                        <svg
                          className={`w-5 h-5 transform transition-transform flex-shrink-0 ${
                            openItems.includes(index) ? "rotate-180" : ""
                          } ${isDark ? "text-gray-400" : "text-blue-500"}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {openItems.includes(index) && (
                        <div
                          className={`px-6 pb-6 ${
                            isDark ? "text-gray-300" : "text-blue-800"
                          }`}
                        >
                          <p className="leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <div
                      className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                        isDark ? "bg-gray-700" : "bg-blue-100"
                      }`}
                    >
                      <svg
                        className={`w-8 h-8 ${isDark ? "text-gray-400" : "text-blue-500"}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3
                      className={`text-lg font-semibold mb-2 ${
                        isDark ? "text-white" : "text-blue-800"
                      }`}
                    >
                      No questions found
                    </h3>
                    <p
                      className={`${isDark ? "text-gray-400" : "text-blue-700"}`}
                    >
                      Try adjusting your search terms or browse different
                      categories.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Help */}
            <div
              className={`mt-8 rounded-xl shadow-lg p-6 ${
                isDark
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-white border border-blue-200 shadow-blue-100"
              }`}
            >
              <h2
                className={`text-xl font-semibold mb-4 ${
                  isDark ? "text-white" : "text-blue-800"
                }`}
              >
                Need More Help?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div
                  className={`p-4 rounded-lg text-center ${
                    isDark ? "bg-gray-700" : "bg-blue-50"
                  }`}
                >
                  <div
                    className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                      isDark ? "bg-blue-600" : "bg-blue-100"
                    }`}
                  >
                    <svg
                      className={`w-6 h-6 ${isDark ? "text-white" : "text-blue-600"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                      />
                    </svg>
                  </div>
                  <h3
                    className={`font-semibold mb-2 ${
                      isDark ? "text-white" : "text-blue-800"
                    }`}
                  >
                    Community Forum
                  </h3>
                  <p
                    className={`text-sm mb-3 ${
                      isDark ? "text-gray-400" : "text-blue-700"
                    }`}
                  >
                    Get help from other FindWork users
                  </p>
                  <button
                    onClick={handleNavigateToCommunity}
                    className={`text-sm font-medium ${
                      isDark
                        ? "text-blue-400 hover:text-blue-300"
                        : "text-blue-600 hover:text-blue-800"
                    }`}
                  >
                    Join Discussion →
                  </button>
                </div>

                <div
                  className={`p-4 rounded-lg text-center ${
                    isDark ? "bg-gray-700" : "bg-blue-50"
                  }`}
                >
                  <div
                    className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                      isDark ? "bg-blue-600" : "bg-blue-100"
                    }`}
                  >
                    <svg
                      className={`w-6 h-6 ${isDark ? "text-white" : "text-blue-600"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  </div>
                  <h3
                    className={`font-semibold mb-2 ${
                      isDark ? "text-white" : "text-blue-800"
                    }`}
                  >
                    Help Center
                  </h3>
                  <p
                    className={`text-sm mb-3 ${
                      isDark ? "text-gray-400" : "text-blue-700"
                    }`}
                  >
                    Detailed guides and tutorials
                  </p>
                  <Link
                    to="/help-center"
                    className={`text-sm font-medium ${
                      isDark
                        ? "text-blue-400 hover:text-blue-300"
                        : "text-blue-600 hover:text-blue-800"
                    }`}
                  >
                    Read Guides →
                  </Link>
                </div>

                <div
                  className={`p-4 rounded-lg text-center ${
                    isDark ? "bg-gray-700" : "bg-blue-50"
                  }`}
                >
                  <div
                    className={`w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center ${
                      isDark ? "bg-blue-600" : "bg-blue-100"
                    }`}
                  >
                    <svg
                      className={`w-6 h-6 ${isDark ? "text-white" : "text-blue-600"}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <h3
                    className={`font-semibold mb-2 ${
                      isDark ? "text-white" : "text-blue-800"
                    }`}
                  >
                    Contact Support
                  </h3>
                  <p
                    className={`text-sm mb-3 ${
                      isDark ? "text-gray-400" : "text-blue-700"
                    }`}
                  >
                    Get personalized help from our team
                  </p>
                  <button
                    onClick={handleContactSupport}
                    className={`text-sm font-medium ${
                      isDark
                        ? "text-blue-400 hover:text-blue-300"
                        : "text-blue-600 hover:text-blue-800"
                    }`}
                  >
                    Get Support →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
